import React, { useEffect, useState } from 'react';

import { Banner } from '@buildoutinc/blueprint-react/ui/Banner';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleInfo } from '@fortawesome/pro-solid-svg-icons';

import { Button } from 'components/ui/button';
import LoadingSpinner from 'components/ui/loading_spinner';
import { Modal } from '@buildoutinc/blueprint-react/ui/Modal';
import PropertyMergeTable from './property_merge_table';
import { allRequiredSelected, displayedKeys } from './helpers';
import { getMerge } from './api';

interface Props {
  propertyIds: number[];
  saving?: boolean;
  onClose: () => void;
  onMerge: (
    properties: Record<string, any>[],
    attributesMap: Record<string, number | null>,
    shownKeys: string[]
  ) => void;
}

export default function PropertyMergeStep({ propertyIds, saving, onClose, onMerge }: Props) {
  const [properties, setProperties] = useState<Record<string, any>[]>([]);
  const [propertyDefaults, setPropertyDefaults] = useState<Record<string, any>>({});
  const [attributesMap, setAttributesMap] = useState<Record<string, number | null>>({});

  useEffect(() => {
    async function fetchProperties() {
      const response = await getMerge(propertyIds);
      setProperties(response.properties);
      setPropertyDefaults(response.propertyDefaults);
    }

    fetchProperties();
  }, []);

  const shownKeys = displayedKeys(properties, propertyDefaults);

  const handleSelectKey = (key: string, id: number) => setAttributesMap(prev => ({ ...prev, [key]: id }));

  const handleSelectAll = (id: number) => {
    setAttributesMap((prev) => {
      const next = { ...prev };
      shownKeys.forEach((key) => {
        next[key] = id;
      });
      return next;
    });
  };

  const disabled = !properties.length || saving || !allRequiredSelected(attributesMap, shownKeys);

  const content = <>
    <Banner className="align-items-center" variant="info">
      <Banner.Content>
        <FontAwesomeIcon icon={faCircleInfo} size="lg" />
        <div className="d-flex flex-column gap-3 flex-fill">
          <div>
            In the table below, you will see all conflicting information between the properties you
            have selected to merge. For each row, select which data you would like to keep.
          </div>
          <div>
            Note that all associations to contacts, comps, activities, etc. will be preserved.
          </div>
        </div>
      </Banner.Content>
    </Banner>

    <PropertyMergeTable
      attributesMap={attributesMap}
      properties={properties}
      propertyDefaults={propertyDefaults}
      onSelectAll={handleSelectAll}
      onSelectKey={handleSelectKey}
    />
  </>;

  return <>
    <Modal.Body className="d-flex flex-column gap-3">
      {properties.length ? content : <LoadingSpinner/>}
    </Modal.Body>
    <Modal.Footer>
      <Button label="Cancel" variant="ghost" onClick={onClose} />
      <Button disabled={disabled} label="Merge" onClick={() => onMerge(properties, attributesMap, shownKeys)} />
    </Modal.Footer>
  </>;
}
